import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
  useLazyGetPaginatedUsersQuery,
  useUpdateUserArchiveMutation,
} from 'app/services/user.service'

import { FixMeLater } from 'types'
import { IUser } from 'types/user.types'
import colors from 'utils/colors'
import { RESEND_INVITE_DESCRIPTION, CONFIRM_RESEND_INVITE } from 'utils/constants'
import UserInfoModal from './user-info/UserInfoModal'
import CreateUserModal from './create-user/CreateUserModal'
import useInfoModal from 'utils/hooks/useInfoModal'
import SettingsHeader from 'features/settings/SettingsHeader'
import { BUTTONS, MODALS, TABLE, TITLES } from 'locales/locales'

import CustomButton from 'components/button/CustomButton'
import Loading from 'components/spinner/Loading'

import KGrid from 'components/kendo/grid/KGrid'
import { GridColumn } from '@progress/kendo-react-grid'
import { Button } from '@progress/kendo-react-buttons'
import { FaCheck, FaInfoCircle, FaTrash } from 'react-icons/fa'
import { TableWrapper } from '../channel-management/ChannelManagement'
import { useAppSelector } from 'app/hooks'
import { IRolePermissions, UserManagementPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'



const UserManagement = () => {
  const { t } = useTranslation()
  const [userId, setUserId] = useState('')
  const { openModal, closeModal, Modal } = useInfoModal()
  const [currentData, setCurrentData] = useState({ data: [] as FixMeLater[], totalCount: 1 })
  const [isCreateModalVisible, setisCreateModalVisible] = useState(false)
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false)
  const [getPaginatedUsers] = useLazyGetPaginatedUsersQuery()
  const [updateUserArchiveById] = useUpdateUserArchiveMutation()

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const [dataState, setDataState] = useState({
    take: 10,
    skip: 0,
    filter: {
      logic: 'and',
      filters: [],
    },
    sort: [],
  })

  const hasUserUpdatePermission = isFlagSetIn(
    [UserManagementPermissions.UPDATE_USER],
    permissions.userManagement,
  )
  const hasUserCreateAndArchivePermission = isFlagSetIn(
    [UserManagementPermissions.CREATE_AND_ARCHIVE_USER],
    permissions.userManagement,
  )

  useEffect(() => {
    fetchUsers()
  }, [dataState])

  const fetchUsers = async () => {
    try {
      const data = await getPaginatedUsers({
        ...dataState,
        count: true,
      })

      const newArr = data?.data?.users.map((user: IUser) => ({
        ...user,
        actions: {
          actionKey: user?.isDeleted
            ? { text: t(TABLE.USER_ACTIVE), color: colors.vime, icon: FaCheck }
            : { text: t(TABLE.USER_DEACTIVE), color: colors.red, icon: FaTrash },
        },
      }))

      if (data.data) setCurrentData({ data: newArr, totalCount: data.data?.totalCount || 0 })
    } catch (error) {
      console.log(error)
    }
  }

  const onToggleArchive = (params: IUser, index: number) => {
    if (currentData?.data?.[index]) {
      const { isDeleted } = currentData.data[index]
      openModal({
        title: isDeleted ? t(MODALS.ACTIVE_USER_TITLE) : t(MODALS.INACTIVE_USER_TITLE),
        description: isDeleted
          ? t(MODALS.ACTIVE_USER_DESCRIPTION)
          : t(MODALS.INACTIVE_USER_DESCRIPTION),
        cancelTitle: t(BUTTONS.CANCEL) as string,
        okTitle: t(BUTTONS.YES) as string,
        onCancel: onCancelDeleteUser,
        onOk: () => onConfirmDeleteUser(params, !isDeleted),
      })
    }
  }

  const onCloseCreateUserModal = () => {
    setisCreateModalVisible(false)
    fetchUsers()
  }

  const onEdit = (params: IUser) => {
    setUserId(params._id)
    setIsInfoModalVisible(true)
  }

  // const onResendInvite = (params: IUser) => {
  //   setUserId(params._id)
  //   openModal({
  //     title: CONFIRM_RESEND_INVITE,
  //     description: RESEND_INVITE_DESCRIPTION,
  //     cancelTitle: t(BUTTONS.CANCEL) as string,
  //     okTitle: t(BUTTONS.SAVE) as string,
  //     onCancel: closeModal,
  //     onOk: () => onConfirmResendInvite(params.email),
  //   })
  // }

  const onAction = (params: IUser, index: number) => {
    onToggleArchive(params, index)
  }

  const onConfirmDeleteUser = async (user: IUser, isDeleted: boolean) => {
    try {
      Loading.ref.show()
      const userId = user._id
      await updateUserArchiveById({ userId, isDeleted })
      fetchUsers()
    } finally {
      closeModal()
      Loading.ref.hide()
    }
  }

  const onConfirmResendInvite = async (email: string) => {
    try {
      Loading.ref.show()
      closeModal()
      fetchUsers()
    } finally {
      Loading.ref.hide()
    }
  }

  const onCancelDeleteUser = () => {
    closeModal()
  }

  const onClickAddNewUser = () => {
    setisCreateModalVisible(true)
  }

  const onCloseInfoModal = () => {
    setIsInfoModalVisible(false)
  }



  const ActionCell: React.FC<FixMeLater> = ({ dataItem, dataIndex }) => {
    const { t } = useTranslation()
    return (
      <td>
        <ButtonContainer>
          {hasUserUpdatePermission ? (
            <Button
              onClick={() => {
                onEdit(dataItem)
              }}
              aria-label={t(dataItem.actions.actionKey.text)}
            >
              <FaInfoCircle />
              {t(TABLE.INFO)}
            </Button>
          ) : null}
          {hasUserCreateAndArchivePermission ? (
            <Button
              style={{ color: dataItem.actions.actionKey.color }}
              onClick={() => {
                onAction(dataItem, dataIndex)
              }}
              aria-label={t(dataItem.actions.actionKey.text)}
            >
              {dataItem.actions.actionKey.icon && <dataItem.actions.actionKey.icon />}
              {t(dataItem.actions.actionKey.text)}
            </Button>
          ) : null}
        </ButtonContainer>
      </td>
    )
  }


  
  const handleFilterChange = (event: FixMeLater) => {
    setDataState({
      ...dataState,
      filter: event.filter,
    })
  }

  const onDataStateChange = (event: FixMeLater) => {
    const updatedState = {
      ...dataState,
      skip: event.dataState.skip,
      take: event.dataState.take,
      sort: event.dataState.sort,
    }
    setDataState(updatedState)
  }

 

  return (
    <Container>
      <Wrapper>
        <TopContainer>
          <SettingsHeader title={t(TITLES.USER_SETTINGS_TITLE)} />
          {hasUserCreateAndArchivePermission ? (
            <CustomButton
              text={t(BUTTONS.CREATE_USER)}
              textColor="white"
              onClick={onClickAddNewUser}
            />
          ) : null}
        </TopContainer>
        <TableWrapper>
          <KGrid
            //loading={loading}
            datasource={currentData.data}
            dataState={dataState}
            pageable={true}
            total={currentData.totalCount | 0}
            filterable={true}
            editMode={false}
            sortable={true}
            onFilterChange={handleFilterChange}
            onDataStateChange={onDataStateChange}
          >
            <GridColumn field="fullname" title={t(TABLE.USER_FULLNAME)}></GridColumn>
            <GridColumn field="email" title={t(TABLE.USER_EMAIL)}></GridColumn>
            <GridColumn
              filterable={true}
              sortable={true}
              field="isDeleted"
              filter="boolean"
              cell={({ dataItem }) => (
                <td>{dataItem.isDeleted ? t(TABLE.INACTIVE) : t(TABLE.ACTIVE)}</td>
              )}
              title={t(TABLE.USER_STATUS)}
            />

            <GridColumn
              filterable={true}
              sortable={false}
              field="roles"
              cell={({ dataItem }) => {
                return <td>{dataItem?.roles?.length ? dataItem?.roles?.join(', ') : '-'}</td>
              }}
              title={t(TABLE.USER_TYPE)}
            />
            <GridColumn
              field="actions"
              filterable={false}
              sortable={false}
              title={t(TABLE.USER_ACTIONS)}
              cell={({ dataIndex, dataItem }) => (
                <ActionCell dataIndex={dataIndex % 10} dataItem={dataItem} />
              )}
            />
          </KGrid>
        </TableWrapper>
        {isCreateModalVisible && (
          <CreateUserModal onCancel={onCloseCreateUserModal} onClose={onCloseCreateUserModal} />
        )}
        {isInfoModalVisible && (
          <UserInfoModal
            userId={userId}
            onClose={onCloseInfoModal}
            fetchUsers={fetchUsers}
          />
        )}
        {Modal}
      </Wrapper>
    </Container>
  )
}

export default UserManagement

const Container = styled.div`
  width: 100%;
`

const Wrapper = styled.div`
  padding: 0px 30px;
  width: 100%;
  &::-webkit-scrollbar {
    display: none;
  }
`
const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`
const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  button span {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.8em;
  }
`
